import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { TEST_SCRIPT, type ScriptSpeaker } from '../lib/test-script';
import { Icon } from './Icon';

/**
 * FELOLVASO NEZET - az atirat-meres szovege, hivas kozben olvashatoan.
 *
 * Aki felolvas, az nem gorget es nem hunyorog: nagy betu, szeles sorkoz, es
 * elsore latszik, KI olvassa az adott reszt, es mi az utasitas (csend,
 * egymasba beszeles, telefon). A szamok ki vannak emelve - ezeken mulik a
 * meres legjobban, es ezeket a legkonnyebb felreolvasni.
 */

const SPEAKER: Record<ScriptSpeaker, { label: string; className: string }> = {
  A: { label: 'A olvassa', className: 'bg-accent text-accent-ink' },
  B: { label: 'B olvassa', className: 'bg-[oklch(84%_0.17_85)] text-[oklch(28%_0.06_60)]' },
  telefon: { label: 'Telefonon: B', className: 'bg-[oklch(62%_0.15_155)] text-white' },
  mindketten: { label: 'Mindketten', className: 'bg-surface-sunken text-ink' },
};

const SIZES = ['text-base', 'text-lg', 'text-xl', 'text-2xl', 'text-3xl'] as const;
const SIZE_KEY = 'avslack.testScript.size';

function readSize(): number {
  try {
    const value = Number(localStorage.getItem(SIZE_KEY));
    return Number.isInteger(value) && value >= 0 && value < SIZES.length ? value : 2;
  } catch {
    return 2;
  }
}

/** A szamjegy-csoportok (telefonszam, adoszam, osszeg) kiemelve. */
function withNumbers(text: string): ReactNode {
  // Csak az ONALLO szam: a "Doki4Vet" negyese nem szam, hanem a nev resze.
  const parts = text.split(/(?<!\p{L})(\+?\d[\d\s\-.,]*\d|\d)(?!\p{L})/gu);
  // A `split` a talalt szamokat a PARATLAN helyekre teszi - csak azokat emeljuk ki.
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <span
        key={index}
        className="rounded bg-accent-soft px-1 font-semibold tabular-nums text-accent"
      >
        {part}
      </span>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}

function SpeakerTag({ speaker }: { speaker: ScriptSpeaker }) {
  const info = SPEAKER[speaker];
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-pill px-2.5 py-0.5 text-xs font-bold ${info.className}`}
    >
      {info.label}
    </span>
  );
}

/** Visszaszamlalo a csendszakaszhoz: ne kelljen orat nezni hivas kozben. */
function SilenceTimer({ seconds }: { seconds: number }) {
  const [left, setLeft] = useState<number | null>(null);

  useEffect(() => {
    if (left === null || left <= 0) return;
    const timer = setTimeout(() => setLeft(left - 1), 1000);
    return () => clearTimeout(timer);
  }, [left]);

  if (left === null) {
    return (
      <button
        type="button"
        onClick={() => setLeft(seconds)}
        className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink hover:bg-accent-hover"
      >
        {seconds} mp csend indítása
      </button>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <span className="font-display text-4xl font-bold tabular-nums">
        {left > 0 ? left : 'Kész'}
      </span>
      {left > 0 ? (
        <span className="text-sm text-ink-muted">másodperc csend — senki ne szóljon</span>
      ) : (
        <button
          type="button"
          onClick={() => setLeft(null)}
          className="text-sm text-ink-muted underline underline-offset-2"
        >
          újra
        </button>
      )}
    </div>
  );
}

export function TestScriptReader({ onClose }: { onClose?: () => void }) {
  const [size, setSize] = useState(readSize);
  const [full, setFull] = useState(false);

  const changeSize = (delta: number) => {
    const next = Math.min(SIZES.length - 1, Math.max(0, size + delta));
    setSize(next);
    try {
      localStorage.setItem(SIZE_KEY, String(next));
    } catch {
      // A meret csak kenyelem - tarolo nelkul is mukodik.
    }
  };

  const body = (
    <div className="flex h-full min-h-0 flex-col bg-surface">
      <header className="flex items-center gap-2 border-b border-border-soft px-4 py-2">
        <Icon name="atirat" />
        <p className="min-w-0 flex-1 truncate text-sm font-semibold">Felolvasandó szöveg</p>
        <button
          type="button"
          onClick={() => changeSize(-1)}
          disabled={size === 0}
          aria-label="Kisebb betű"
          className="rounded-control px-2 py-1 text-xs font-bold text-ink-muted hover:bg-surface-hover disabled:opacity-40"
        >
          A−
        </button>
        <button
          type="button"
          onClick={() => changeSize(1)}
          disabled={size === SIZES.length - 1}
          aria-label="Nagyobb betű"
          className="rounded-control px-2 py-1 text-base font-bold text-ink-muted hover:bg-surface-hover disabled:opacity-40"
        >
          A+
        </button>
        <button
          type="button"
          onClick={() => setFull((current) => !current)}
          className="rounded-control px-2 py-1 text-xs font-semibold text-ink-muted hover:bg-surface-hover"
        >
          {full ? 'Kisebb nézet' : 'Teljes képernyő'}
        </button>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Bezárás"
            className="rounded-control px-1.5 py-1 text-ink-faint hover:bg-surface-hover hover:text-ink"
          >
            <Icon name="bezaras" />
          </button>
        )}
      </header>

      <ol
        className={`min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-5 py-5 ${SIZES[size]}`}
      >
        {TEST_SCRIPT.map((part, index) => (
          <li key={part.title} className="mx-auto max-w-3xl">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="font-display text-sm font-bold text-ink-faint">
                {index + 1}. {part.title}
              </span>
              <SpeakerTag speaker={part.speaker} />
            </div>

            {part.instruction && (
              <p className="mb-3 rounded-control border-l-4 border-[oklch(84%_0.17_85)] bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
                {part.instruction}
              </p>
            )}

            {part.silenceSeconds && <SilenceTimer seconds={part.silenceSeconds} />}

            <div className="space-y-3">
              {part.lines.map((line) => (
                <p key={line.text} className="leading-relaxed text-ink">
                  {line.speaker && (
                    <span className="mr-2 align-middle">
                      <SpeakerTag speaker={line.speaker} />
                    </span>
                  )}
                  {withNumbers(line.text)}
                </p>
              ))}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );

  /*
    TELJES KEPERNYO: a hivas ablakat takarja, a vezerlok nelkul. A hivas
    kozben is mukodik - a felolvaso nem veszit el semmit, csak a szoveget latja.
  */
  if (full) return <FullScreen onClose={() => setFull(false)}>{body}</FullScreen>;
  return body;
}

/**
 * A lap TETEJERE kerul (portal), nem a szulo ele.
 *
 * Egy atalakitott szulo (pl. animacio) a `fixed` elemet a sajat dobozaba
 * zarja - ott csak a fotartalom felett latszana, az oldalsav kilogna melle.
 */
export function FullScreen({
  children,
  onClose,
  label = 'Felolvasandó szöveg',
}: {
  children: ReactNode;
  /** Escape-re hivodik. */
  onClose: () => void;
  label?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  /*
    A FOKUSZ belekerul a nezetbe, es zaraskor visszater oda, ahonnan jott -
    kulonben a billentyuzetes hasznalo a takart lap mogott tabolna tovabb.
  */
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    return () => {
      if (previous?.isConnected) previous.focus();
    };
  }, []);

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        // Egymasba agyazott teljes kepernyonel (a felolvaso a meres folott)
        // csak a LEGFELSO zarodjon - a React-esemeny a portalon at is buborekol.
        event.stopPropagation();
        onClose();
      }}
      className="fixed inset-0 z-50 overscroll-contain outline-none"
    >
      {children}
    </div>,
    document.body,
  );
}
